import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import * as d3 from "d3";
import { Grid } from "@mui/material";
import { MACgraphModel } from "./data/MACgraphModel";
import { Constants } from "constants/constant";
import { downloadChartImage } from "utilities/downloadChartImage";
import {
  layoutLegend,
  renderLegendInto,
  CHART_PALETTE,
  CHART_MIN_WIDTH,
  CHART_LEGEND_TOP_GAP,
  CHART_BOTTOM_PAD,
} from "utilities/chartLegendLayout";
import {
  applyTooltipStyle,
  escapeHtml,
  positionTooltip,
} from "utilities/chartTooltip";

type MarginalAbatementCostCurveProps = {
  data: MACgraphModel[];
  labelKey: "locationName" | "reductionMeasureName";
  currency: string;
};

export type MarginalAbatementCostCurveRef = {
  downloadChart: (format: "png" | "jpeg") => void;
};

const DEFAULT_WIDTH = 1100;
const PLOT_HEIGHT = 380;
const MARGIN = { top: 24, right: 24, left: 90 };
const AXIS_LABEL_OFFSET = 44;
const EMPTY_STATE_HEIGHT = 240;

const MarginalAbatementCostCurve = forwardRef<
  MarginalAbatementCostCurveRef,
  MarginalAbatementCostCurveProps
>(({ data, labelKey, currency }, ref) => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(DEFAULT_WIDTH);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const update = () =>
      setWidth(Math.max(element.clientWidth || DEFAULT_WIDTH, CHART_MIN_WIDTH));
    update();

    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const safeData = useMemo(
    () =>
      Array.isArray(data)
        ? data.filter(
            (d) =>
              d &&
              Number.isFinite(Number(d.x1)) &&
              Number.isFinite(Number(d.x2)) &&
              Number.isFinite(Number(d.y1)) &&
              Number.isFinite(Number(d.y2)) &&
              Number.isFinite(Number(d.mac))
          )
        : [],
    [data]
  );

  // One legend entry per distinct series rather than per data row: a measure
  // split across several MAC segments previously produced a duplicate entry
  // for every segment.
  const uniqueLabels = useMemo(() => {
    const seen = new Set<string>();
    const labels: string[] = [];
    (Array.isArray(data) ? data : []).forEach((d) => {
      const label = String(d?.[labelKey] ?? "Unknown");
      if (!seen.has(label)) {
        seen.add(label);
        labels.push(label);
      }
    });
    return labels;
  }, [data, labelKey]);

  const colorScale = useMemo(
    () => d3.scaleOrdinal(CHART_PALETTE).domain(uniqueLabels),
    [uniqueLabels]
  );

  const innerWidth = width - MARGIN.left - MARGIN.right;

  const legend = useMemo(
    () =>
      layoutLegend(
        uniqueLabels.map((label) => ({ label, color: colorScale(label) })),
        innerWidth
      ),
    [uniqueLabels, colorScale, innerWidth]
  );

  const legendTop =
    MARGIN.top + PLOT_HEIGHT + AXIS_LABEL_OFFSET + CHART_LEGEND_TOP_GAP;
  // The legend is the last thing drawn, so the canvas ends just below it.
  const totalHeight =
    safeData.length === 0
      ? EMPTY_STATE_HEIGHT
      : legendTop + legend.height + CHART_BOTTOM_PAD;

  useEffect(() => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    if (safeData.length === 0) {
      svg
        .append("text")
        .attr("x", width / 2)
        .attr("y", 120)
        .attr("text-anchor", "middle")
        .attr("fill", "#64748b")
        .style("font-size", "16px")
        .text("No valid data available for Marginal Abatement Cost chart.");
      return;
    }

    const maxX = d3.max(safeData, (d) => Number(d.x2));
    const minY = d3.min(safeData, (d) => Number(d.y1));
    const maxY = d3.max(safeData, (d) => Number(d.y2));

    const xScale = d3
      .scaleLinear()
      .domain([0, Number(maxX) * 1.05])
      .range([0, innerWidth]);

    const yScale = d3
      .scaleLinear()
      .domain([Number(minY), Number(maxY)])
      .range([PLOT_HEIGHT, 0]);

    const g = svg
      .append("g")
      .attr("class", "mac-plot")
      .attr("transform", `translate(${MARGIN.left}, ${MARGIN.top})`);

    const tooltip = applyTooltipStyle(
      d3.select("body").append("div").attr("class", "chart-tooltip")
    ).style("opacity", "0");

    const MIN_BAR_SIZE = 2;

    g.selectAll("rect")
      .data(safeData)
      .enter()
      .append("rect")
      .attr("x", (d) => {
        const scaledX1 = xScale(Number(d.x1));
        const scaledX2 = xScale(Number(d.x2));
        const baseX = Math.min(scaledX1, scaledX2);
        return scaledX1 === scaledX2 ? baseX - MIN_BAR_SIZE / 2 : baseX;
      })
      .attr("y", (d) => {
        const mac = Number(d.mac);
        if (mac < 0) return yScale(0);
        if (mac === 0) return yScale(0) - MIN_BAR_SIZE / 2;
        return yScale(Number(d.y2));
      })
      .attr("width", (d) => {
        const w = Math.abs(xScale(Number(d.x2)) - xScale(Number(d.x1)));
        return w < MIN_BAR_SIZE ? MIN_BAR_SIZE : w;
      })
      .attr("height", (d) => {
        const h = Math.abs(yScale(Number(d.y1)) - yScale(Number(d.y2)));
        return h < MIN_BAR_SIZE ? MIN_BAR_SIZE : h;
      })
      .attr("fill", (d) => colorScale(String(d[labelKey] ?? "Unknown")))
      .on("mouseover", (event, d) => {
        tooltip.transition().duration(200).style("opacity", "0.98");
        tooltip.html(
          `<div style="font-weight:600;margin-bottom:6px">${escapeHtml(
            d[labelKey] ?? "Unknown"
          )}</div>` +
            `MAC: ${escapeHtml(Number(d.mac))} ${escapeHtml(currency)}/${
              Constants.UNIT_TCO2E
            }<br>` +
            `Annual avoided emissions: ${escapeHtml(
              Number(d.annualEmissionReduction ?? 0)
            )} ${Constants.UNIT_TCO2E}`
        );
        positionTooltip(tooltip.node(), event);
      })
      .on("mousemove", (event) => {
        positionTooltip(tooltip.node(), event);
      })
      .on("mouseout", () => {
        tooltip.transition().duration(500).style("opacity", "0");
      });

    g.append("g")
      .attr("transform", `translate(0,${PLOT_HEIGHT})`)
      .call(d3.axisBottom(xScale))
      .selectAll("text")
      .style("font-size", "12px")
      .style("fill", "#7A7A9D");

    g.append("text")
      .attr("class", "axis-label")
      .attr("x", innerWidth / 2)
      .attr("y", PLOT_HEIGHT + AXIS_LABEL_OFFSET)
      .attr("text-anchor", "middle")
      .text(`Annual Avoided Emissions (${Constants.UNIT_TCO2E})`)
      .style("font-size", "18px")
      .style("font-weight", "500")
      .style("fill", "#425466");

    g.append("g")
      .call(d3.axisLeft(yScale))
      .selectAll("text")
      .style("font-size", "12px")
      .style("fill", "#7A7A9D");

    g.append("text")
      .attr("class", "axis-label")
      .attr("transform", "rotate(-90)")
      .attr("x", -PLOT_HEIGHT / 2)
      .attr("y", -MARGIN.left / 1.4)
      .attr("text-anchor", "middle")
      .text(`MAC (${currency}/${Constants.UNIT_TCO2E})`)
      .style("font-size", "18px")
      .style("font-weight", "500")
      .style("fill", "#425466");

    // Legend: fixed columns beneath the plot, aligned to the plot area and
    // sharing its width so chart and legend can never drift apart.
    renderLegendInto(svg, legend, MARGIN.left, legendTop);

    return () => {
      tooltip.remove();
    };
  }, [
    safeData,
    labelKey,
    currency,
    colorScale,
    legend,
    innerWidth,
    legendTop,
    width,
  ]);

  const handleDownload = (format: "png" | "jpeg") => {
    // The SVG already carries the legend, so the exporter only has to rasterise
    // it. This keeps the export byte-identical to the screen.
    downloadChartImage({
      svgRef,
      format,
      fileName: `mac-curve-chart.${format}`,
    });
  };

  useImperativeHandle(ref, () => ({
    downloadChart: handleDownload,
  }));

  return (
    <Grid item xs={12} sx={{ mb: 1, width: "100%", maxWidth: "100%" }}>
      <div ref={containerRef} style={{ width: "100%" }}>
        <svg
          ref={svgRef}
          width={width}
          height={totalHeight}
          viewBox={`0 0 ${width} ${totalHeight}`}
          style={{ maxWidth: "100%", height: "auto" }}
        />
      </div>
    </Grid>
  );
});

export default MarginalAbatementCostCurve;